import { useTranslation } from 'react-i18next'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { useLocation, useNavigate } from 'react-router-dom'
import { useContext, useEffect, useState, useRef } from 'react'

import { Breadcrumbs, Button, LinearProgress, ListItemButton, Typography } from '@mui/material'

import { config } from '../config'
import { countriesCurrencies } from '../lib'
import { useSocket } from '../hooks/use-socket'
import Dialog from '../components/common/dialog'
import NextIcon from '../assets/icons/next-icon.svg'
import HelpIcon from '../assets/icons/help-icon.svg'
import { Customer, FormError, Payment } from '../types'
import Pagination from '../components/common/pagination'
import { getPaymentsCompletedData } from '../api/app-data.api'
import { AppStateContext, AuthContext, UserContext } from '../contexts'
import { checkTwoFactorAuth, requestAccessToken } from '../api/auth.api'
import { NotificationsContext } from '../contexts/notifications.context'
import { FiltersOptionsModal } from '../components/common/filters-options-modal'
import { downloadPaymentVoucher, sendReviewedPayment } from '../api/payments.api'
import { ActionsBar } from '../components/payments-to-review-management/actions-bar'
import { normalizePaymentData, handleSyncDataToLocalDB, handleCheckIfOtpCodeIsValid, upsertManyWithLimitAtStartFast, upsertPaymentWithLimitAtStart } from '../utils'
import { PaymentCompletedListItem } from '../components/payments-completed-management/payment-completed-list-item'
import CustomerDetailsPaymentDetailsDataTable from '../components/customer-details-management/customer-details-payment-details-data-table'
import { baseButtonBlueBg } from '../theme/custom-styles'
import { searchPaymentsByRegex } from '../api/app-queries.api'

const PaymentsCompletedManagement = () => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

  const navigate = useNavigate()
  const location = useLocation()
  const { userSession } = useContext(UserContext)
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { setScrollToTop, processingBatch } = useContext(AppStateContext)
  const { updateNewNotificationStatus } = useContext(NotificationsContext)
  const { t } = useTranslation(['payments-to-review-management', 'two-factor-auth', 'document-payments-details-management', 'customer-details-management', 'common'])

  const currentListPageRef = useRef<number>(1)
  const paymentToSearchRef = useRef<string>('')
  const isComponentMount = useRef<boolean>(false)
  const currentLoadingDataRef = useRef<boolean>(true)
  const paymentStatusAllowedRef = useRef<Array<string>>([])
  const currentPaymentsDataRef = useRef<Array<Payment>>([])
  const noRegistriesFoundAtDatabaseRef = useRef<boolean>(false)

  const [otpCode, setOtpCode] = useState('')
  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [paymentToSearch, setPaymentToSearch] = useState<string>('')
  const [customersData, setCustomersData] = useState<Array<Customer>>([])
  const [loadingLocalData, setLoadingLocalData] = useState<boolean>(true)
  const [paymentsList, setPaymentsList] = useState<Array<Partial<Payment>>>([])
  const [paymentsData, setPaymentsData] = useState<Array<Partial<Payment>>>([])
  const [searchingInDatabase, setSearchingInDatabase] = useState<boolean>(false)
  const [searchedPayments, setSearchedPayments] = useState<Array<Partial<Payment>>>([])
  const [paymentDetailsIsLoading, setPaymentDetailsIsLoading] = useState<boolean>(false)
  const [openPaymentDetailsModal, setOpenPaymentDetailsModal] = useState<boolean>(false)
  const [openFiltersOptionsModal, setOpenFiltersOptionsModal] = useState<boolean>(false)
  const [noRegistriesFoundAtLocal, setNoRegistriesFoundAtLocal] = useState<boolean>(false)
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<Partial<Payment>>({})
  const [noRegistriesFoundAtDatabase, setNoRegistriesFoundAtDatabase] = useState<boolean>(false)
  const [openSendPaymentConfimationModal, setOpenSendPaymentConfimationModal] = useState<boolean>(false)
  const [dialogSendPaymentConfimationIsLoading, setDialogSendPaymentConfimationIsLoading] = useState<boolean>(false)

  useEffect(() => {
    if (!appDataSocket) return

    if (!userSession) return

    paymentStatusAllowedRef.current = location.pathname.includes('payments-completed') ? ['paid'] : ['validated']

    appDataSocket.on(`newPaymentAdded:${selectedCurrency}`, async (dataFromSocket: any) => {
      if (!paymentStatusAllowedRef.current.includes(dataFromSocket.status)) {
        handleDeleteAndSetPayments(String(dataFromSocket._id))
        return
      }

      handleUpdateAndSetPayments(dataFromSocket)
    })

    appDataSocket.on(`paymentUpdated:${selectedCurrency}`, async (dataFromSocket: any) => {
      if (!paymentStatusAllowedRef.current.includes(dataFromSocket.status) || (paymentStatusAllowedRef.current.includes(dataFromSocket.status) && dataFromSocket.deleted === true)) {
        handleDeleteAndSetPayments(String(dataFromSocket._id))
        return
      }

      handleUpdateAndSetPayments(dataFromSocket)
    })

    appDataSocket.on(`paymentDeleted:${selectedCurrency}`, async (dataFromSocket: any) => {
      if (paymentStatusAllowedRef.current.includes(dataFromSocket.status)) {
        handleDeleteAndSetPayments(String(dataFromSocket.paymentId))
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    handleGetPaymentsCompleted()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage = paymentToSearch === '' ? paymentsData.slice(firstPageIndex, lastPageIndex) : searchedPayments.slice(firstPageIndex, lastPageIndex)

    setPaymentsList(paymentsPerPage)

    if (processingBatch !== true) {
      for (const element of paymentsPerPage) {
        const paymentToAdd = normalizePaymentData(element)
        handleSyncDataToLocalDB('payments', paymentToAdd)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, paymentsData, searchedPayments])

  const handleDeleteAndSetPayments = (paymentId: string) => {
    const paymentsDataUpdated = currentPaymentsDataRef.current.filter((paymentData: Payment) => String(paymentData._id) !== String(paymentId))
    globalThis.sessionStorage.setItem(`paymentsCompleted:${selectedCountry}`, JSON.stringify({ paymentsData: paymentsDataUpdated }))

    handleSetInitialData({ paymentsData: paymentsDataUpdated })
  }

  const handleUpdateAndSetPayments = (paymentData: Payment) => {
    const localPaymentsCompletedData = globalThis.sessionStorage.getItem(`paymentsCompleted:${selectedCountry}`) || JSON.stringify({ paymentsData: [] })
    const paymentsDataParsed = JSON.parse(localPaymentsCompletedData)
    const paymentsDataUpdated = upsertPaymentWithLimitAtStart(paymentsDataParsed.paymentsData, paymentData, 500)

    handleSetInitialData({ paymentsData: paymentsDataUpdated })
  }

  const handleGetPaymentsCompleted = async () => {
    let paymentsCompletedDataToSet: any = {}
    const localPaymentsCompletedData = globalThis.sessionStorage.getItem(`paymentsCompleted:${selectedCountry}`)

    if (localPaymentsCompletedData) {
      paymentsCompletedDataToSet = JSON.parse(localPaymentsCompletedData)
      handleSetInitialData(paymentsCompletedDataToSet)
    }

    handleGetPaymentsCompletedFromRemote()
  }

  const handleGetPaymentsCompletedFromRemote = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getPaymentsCompletedData({ selectedCountry, selectedCurrency }, accessToken)

      handleSetInitialData(data)

      globalThis.sessionStorage.setItem(`paymentsCompleted:${selectedCountry}`, JSON.stringify(data))
    } catch (error) {
      console.log('Error handleGetPaymentsCompletedFromRemote > ', error)
    }
  }

  const handleSetInitialData = async (dataToSet: any) => {
    const paymentsToDisplay = location.pathname.includes('payments-completed')
      ? dataToSet.paymentsData.filter((paymentData: Payment) => paymentData.status === 'paid')
      : dataToSet.paymentsData.filter((paymentData: Payment) => paymentData.status !== 'paid')

    if (paymentsToDisplay.length === 0 && currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      currentPaymentsDataRef.current = []
      setLoadingLocalData(false)
      return
    }

    const currentListPageDataState = currentListPageRef.current
    const currentPaymentToSearchState = paymentToSearchRef.current
    let paymentsNormalizedData = []

    for (const element of paymentsToDisplay) {
      const paymentData = { ...element }
      const paymentDataNormalized = normalizePaymentData(paymentData) as Payment

      if (paymentDataNormalized.customerId && !paymentDataNormalized.customerRegisteredFullName) {
        paymentDataNormalized.customerData = {
          registeredFullName: t('empty')
        }
      } else {
        paymentDataNormalized.customerData = paymentData.customerData
      }

      paymentsNormalizedData.push(paymentDataNormalized)
    }

    paymentsNormalizedData.sort((thisPayment: Partial<Payment>, nextPayment: Partial<Payment>) => {
      return Date.parse(String(nextPayment.createdAt)) - Date.parse(String(thisPayment.createdAt))
    })

    if (currentPaymentToSearchState === '' || currentPaymentToSearchState === null) {
      const firstPageIndex = currentListPageDataState === null ? 0 : (currentListPageDataState - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const paymentsPerPage = paymentsNormalizedData.slice(firstPageIndex, lastPageIndex)

      setPaymentsList(paymentsPerPage)

      if (processingBatch !== true) {
        for (const element of paymentsPerPage) {
          const paymentToAdd = normalizePaymentData(element)
          handleSyncDataToLocalDB('payments', paymentToAdd)
        }
      }
    }

    currentPaymentsDataRef.current = paymentsNormalizedData
    setPaymentsData(paymentsNormalizedData)
    setCustomersData(dataToSet.customersData)

    if (currentLoadingDataRef.current === true) {
      currentLoadingDataRef.current = false
      setLoadingLocalData(false)
    }

    if (isComponentMount.current === false) {
      isComponentMount.current = true
    }
  }

  const handleSetPaymentToSearch = (paymentToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1
    paymentToSearchRef.current = paymentToSearchData

    if (noRegistriesFoundAtDatabaseRef.current === true) {
      setNoRegistriesFoundAtDatabase(false)
    }

    setErrors({})
    setCurrentListPage(1)
    setNoRegistriesFoundAtLocal(false)
    setPaymentToSearch(paymentToSearchData)

    if (paymentToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentPaymentsDataState = currentPaymentsDataRef.current ?? []
      const paymentsPerPage = currentPaymentsDataState.slice(firstPageIndex, lastPageIndex)

      setPaymentsList(paymentsPerPage)
      setSearchedPayments([])

      if (showError) setErrors({ paymentToSearch: t('searchField.errors.emptyField') })
      return
    }

    const currentFullPaymentsData = [...currentPaymentsDataRef.current]
    const filteredPayments = currentFullPaymentsData.filter((paymentData) => {
      const customerRegisteredFullName = paymentData.customerData?.registeredFullName ? paymentData.customerData.registeredFullName : ''
      const customerDocumentNumber = paymentData.customerData?.documentNumber ? paymentData.customerData.documentNumber : ''
      const paymentReferenceNumber = paymentData.referenceNumber
      const paymentStatus =
        paymentData.status === 'validated'
          ? t('paymentStatus.validated', { ns: 'customer-details-management' })
          : paymentData.status === 'reviewed'
          ? t('paymentStatus.reviewed', { ns: 'customer-details-management' })
          : paymentData.status === 'register-disperse-completed' || paymentData.status === 'processing'
          ? t('paymentStatus.processing', { ns: 'customer-details-management' })
          : t('paymentStatus.paid', { ns: 'customer-details-management' })

      const customerBanksAccountsData = Array.isArray(paymentData.customerData?.bankAccountsData)
        ? paymentData.customerData?.bankAccountsData.filter((bankAccountData) => {
            return bankAccountData.bankNumber.toLowerCase().includes(paymentToSearchData.toLowerCase())
          })
        : []

      return (
        paymentStatus.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        customerRegisteredFullName.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        customerDocumentNumber.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        paymentReferenceNumber.toLowerCase().includes(paymentToSearchData.toLowerCase()) ||
        customerBanksAccountsData.length > 0
      )
    })

    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const paymentsPerPage = filteredPayments.slice(firstPageIndex, lastPageIndex)

    setPaymentsList(paymentsPerPage)
    setSearchedPayments(filteredPayments)

    if (filteredPayments.length === 0) {
      setNoRegistriesFoundAtLocal(true)
    }
  }

  const handleDownloadPaymentVoucher = async () => {
    try {
      setErrors({})
      setPaymentDetailsIsLoading(true)
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const paymentVoucherDownloaded = await downloadPaymentVoucher(String(selectedPaymentDetails._id), accessTokenData.data.accessToken)

      window.open(paymentVoucherDownloaded.data, '_blank')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }

    setPaymentDetailsIsLoading(false)
  }

  const handleSendPayment = async () => {
    const { otpCodeIsValid, formErrors } = handleCheckIfOtpCodeIsValid(otpCode, t)

    if (!otpCodeIsValid) {
      return setErrors({ ...formErrors })
    }

    try {
      setErrors({})
      setDialogSendPaymentConfimationIsLoading(true)
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await checkTwoFactorAuth(otpCode, accessTokenData.data.accessToken)
    } catch (error: any) {
      console.log(error)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.codeInvalid', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.codeExpired', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      return setDialogSendPaymentConfimationIsLoading(false)
    }

    try {
      setErrors({})
      const accessTokenData = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      await sendReviewedPayment(String(selectedPaymentDetails._id), accessTokenData.data.accessToken)

      setOpenPaymentDetailsModal(false)
      setOpenSendPaymentConfimationModal(false)
      setDialogSendPaymentConfimationIsLoading(false)
      updateNewNotificationStatus?.(true, 'payment-sent-successfully')
    } catch (error: any) {
      console.log(error, error?.response?.data?.message, Object.keys(error))
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else if (error?.response?.status === 404) {
        setErrors({ generalError: t('formGeneralErrors.notFound', { ns: 'common' }) })
      } else if (error?.response?.status === 406) {
        setErrors({ generalError: t('formGeneralErrors.insufficientAdamoBalance', { ns: 'common' }) })
      } else if (error?.response?.status === 400) {
        setErrors({ generalError: t('formGeneralErrors.insufficientBalance', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setDialogSendPaymentConfimationIsLoading(false)
    }
  }

  const handleSearchPaymentsCompletedDataByRegex = async () => {
    try {
      setErrors({})
      setSearchingInDatabase(true)

      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      const { data } = await searchPaymentsByRegex({ searchText: paymentToSearch, status: 'completed', belongsTo: userSession?.belongsTo, selectedCountry, selectedCurrency }, accessToken)
      const paymentsToDisplay = location.pathname.includes('payments-completed')
        ? data.paymentsData.filter((paymentData: Payment) => paymentData.status === 'paid')
        : data.paymentsData.filter((paymentData: Payment) => paymentData.status !== 'paid')

      if (paymentsToDisplay.length === 0) {
        setSearchingInDatabase(false)
        setNoRegistriesFoundAtDatabase(true)
        noRegistriesFoundAtDatabaseRef.current = true
        return
      }

      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */
      paymentsToDisplay.sort((thisDocument: Partial<Payment>, nextDocument: Partial<Payment>) => {
        return Date.parse(String(nextDocument.createdAt)) - Date.parse(String(thisDocument.createdAt))
      })
      setPaymentsList(paymentsToDisplay)
      setSearchedPayments(paymentsToDisplay)
      /* ESTO ES PARA ACTUALIZAR EL LISTADO SOLO CON LOS BUSCADOS */

      const currentPaymentsData = [...currentPaymentsDataRef.current]
      const paymentsDataUpdated = upsertManyWithLimitAtStartFast(currentPaymentsData, paymentsToDisplay, 1000)
      paymentsDataUpdated.sort((thisDocument: Partial<Payment>, nextDocument: Partial<Payment>) => {
        return Date.parse(String(nextDocument.createdAt)) - Date.parse(String(thisDocument.createdAt))
      })

      currentPaymentsDataRef.current = paymentsDataUpdated
      setPaymentsData(paymentsDataUpdated)

      setSearchingInDatabase(false)
      setNoRegistriesFoundAtLocal(false)

      globalThis.sessionStorage.setItem(`paymentsCompleted:${selectedCountry}`, JSON.stringify({ paymentsData: data.paymentsData }))
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }

      setSearchingInDatabase(false)
      return
    }
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        aria-label="breadcrumb"
      >
        {userSession &&
        (userSession.role === 'super-admin-client' || userSession.role === 'admin-client') &&
        location.state?.previousPath &&
        location.state?.previousPath === `customer-company-payments/${userSession.belongsTo}` ? (
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate('/')}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.home')}
            </Typography>
          </ListItemButton>
        ) : (
          <Typography
            key="3"
            className="breadcrumb-inactive"
          >
            {t('breadcrumbs.firstLevel')}
          </Typography>
        )}
        {userSession &&
          (userSession.role === 'super-admin-client' || userSession.role === 'admin-client') &&
          location.state?.previousPath &&
          location.state?.previousPath === `customer-company-payments/${userSession.belongsTo}` && (
            <ListItemButton
              sx={{
                '&.MuiButtonBase-root': {
                  padding: '0px'
                },
                '&.MuiButtonBase-root:hover': {
                  backgroundColor: 'rgba(0,0,0,0)'
                }
              }}
              onClick={() => navigate(`/customer-company-payments/${userSession.belongsTo}`)}
            >
              <Typography
                key="3"
                className="breadcrumb-inactive"
              >
                {t('breadcrumbs.individualPayments')}
              </Typography>
            </ListItemButton>
          )}
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {location.pathname.includes('payments-completed') ? t('breadcrumbs.completedLevel') : t('breadcrumbs.validatedLevel')}
        </Typography>
      </Breadcrumbs>
      <ActionsBar
        errors={errors}
        processingDocument={true}
        paymentsData={paymentsData}
        paymentToSearch={paymentToSearch}
        className="payments-completed-app-bar"
        searchingInDatabase={searchingInDatabase}
        handleClearSearchInput={() => setErrors({})}
        handleSetPaymentToSearch={handleSetPaymentToSearch}
        handleOpenFiltersOptionsModal={() => setOpenFiltersOptionsModal(true)}
      />
      <div className="table-header-container">
        <div className="customer-company-table-column customer-company-table-width-10 display-none-xlg table-header-text">{t('tableHeaders.date')}</div>
        <div className="customer-company-table-column customer-company-table-width-45 table-header-text customer-company-table-width-70-md-down">{t('tableHeaders.name')}</div>
        <div className="customer-company-table-column customer-company-table-width-45 table-header-text customer-company-table-width-30-md-down">{t('tableHeaders.status')}</div>
      </div>
      {loadingLocalData ? (
        <div className="documents-list-container">
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
          <div className="users-management-user-data-container-loader"></div>
        </div>
      ) : (
        <>
          <div className="documents-list-container">
            {paymentsList.map((paymentData: Partial<Payment>) => {
              return (
                <PaymentCompletedListItem
                  key={paymentData._id}
                  paymentData={paymentData}
                  handleOpenPaymentDetailsModal={(selectedPaymentData: Partial<Payment>) => {
                    setOpenPaymentDetailsModal(true)
                    setSelectedPaymentDetails(selectedPaymentData)
                  }}
                />
              )
            })}
          </div>
          <Pagination
            pageSize={pageSize}
            currentPage={currentListPage}
            handlePaginationPageChange={(page: number) => {
              setTimeout(() => {
                setScrollToTop(true)
                setCurrentListPage(page)
                currentListPageRef.current = page
              }, 100)
            }}
            handleChangePageSize={(pageSizeToSet: string) => {
              setScrollToTop(true)
              setPageSize(Number(pageSizeToSet))
            }}
            totalCount={paymentToSearch === '' ? paymentsData.length : searchedPayments.length}
          />
          {noRegistriesFoundAtLocal && (
            <div className="customer-no-data-found-container">
              {searchingInDatabase ? (
                <>
                  <div className="customer-no-data-found-searching-progress-bar">
                    <LinearProgress />
                  </div>
                  <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case3', { ns: 'customer-company-details-management' })}</div>
                </>
              ) : (
                <>
                  {noRegistriesFoundAtDatabase === false ? (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        {t('searchingResultTexts.case1.firstPart', { ns: 'customer-company-details-management' })}{' '}
                        <strong
                          className="customer-no-data-found-no-registries-text-click"
                          onClick={() => handleSearchPaymentsCompletedDataByRegex()}
                        >
                          {t('searchingResultTexts.case1.thirdPart', { ns: 'customer-company-details-management' })}
                        </strong>{' '}
                        {t('searchingResultTexts.case1.fourthPart', { ns: 'customer-company-details-management' })}
                      </div>
                      <Button
                        sx={baseButtonBlueBg}
                        onClick={handleSearchPaymentsCompletedDataByRegex}
                      >
                        {t('searchingResultTexts.expandSearch', { ns: 'customer-company-details-management' })}
                      </Button>
                      {errors.generalError && (
                        <div
                          style={{ marginTop: '0px' }}
                          className="error-message"
                        >
                          {errors.generalError}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="customer-no-data-found-no-registries-text">
                        <img
                          src={HelpIcon}
                          alt="help-icon"
                        />
                      </div>
                      <div className="customer-no-data-found-no-registries-text">{t('searchingResultTexts.case2', { ns: 'customer-company-details-management' })}</div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          {openPaymentDetailsModal && (
            <Dialog
              errors={errors}
              dialogIsLoading={paymentDetailsIsLoading}
              hideActionsButtons={
                userSession?.role?.includes('-builder') ||
                selectedPaymentDetails.status === 'reviewed' ||
                selectedPaymentDetails.status === 'error' ||
                selectedPaymentDetails.status === 'processing' ||
                selectedPaymentDetails.status === 'rejected' ||
                (userSession?.role === 'admin-client' && userSession?.belongsTo === config.app.isliveCompanyId)
              }
              dialogTitle={t('paymentDetailsModal.title', { ns: 'document-payments-details-management' })}
              handleDialogAction={() => {
                if (selectedPaymentDetails.status === 'reviewed') {
                  setOpenSendPaymentConfimationModal(true)
                } else if (selectedPaymentDetails.status === 'paid' || selectedPaymentDetails.status === 'validated' || selectedPaymentDetails.status === 'returned') {
                  handleDownloadPaymentVoucher()
                }
              }}
              handleCloseOptionAction={() => {
                setErrors({})
                setOpenPaymentDetailsModal(false)
                setSelectedPaymentDetails({})
              }}
              handleDialogOpenState={(openState: boolean) => {
                setErrors({})
                setOpenPaymentDetailsModal(openState)
                setSelectedPaymentDetails({})
              }}
              actionButtonText={
                selectedPaymentDetails.status === 'paid' || selectedPaymentDetails.status === 'validated' || selectedPaymentDetails.status === 'returned'
                  ? t('paymentDetailsModal.actionButtonText', { ns: 'document-payments-details-management' })
                  : t('paymentDetailsModal.sendActionButtonText', { ns: 'document-payments-details-management' })
              }
            >
              <CustomerDetailsPaymentDetailsDataTable paymentDetailsData={selectedPaymentDetails} />
            </Dialog>
          )}
          {openSendPaymentConfimationModal && (
            <Dialog
              errors={errors}
              actionButtonWarningStyle
              handleDialogAction={handleSendPayment}
              dialogTitle={t('confirmSendPaymentModal.title')}
              dialogIsLoading={dialogSendPaymentConfimationIsLoading}
              actionButtonText={t('confirmSendPaymentModal.actionButtonText')}
              handleDialogOpenState={(openState: boolean) => {
                if (openState === false) {
                  setErrors({})
                  setOtpCode('')
                }

                setOpenSendPaymentConfimationModal(openState)
              }}
            >
              <div
                className="step"
                style={{ marginTop: '0px', gap: '16px' }}
              >
                <div
                  className="step-instruction-container"
                  style={{ fontSize: '14px', color: '#F59E0B' }}
                >
                  {t('checkCodeInstruction', { ns: 'two-factor-auth' })}
                </div>
                <MuiOtpInput
                  length={6}
                  value={otpCode}
                  onChange={(newValue: string) => setOtpCode(newValue)}
                  className={errors?.otpCodeError ? 'MuiOtpInput-Box-inner MuiOtpInput-Box-error' : 'MuiOtpInput-Box-inner'}
                />
              </div>
            </Dialog>
          )}
        </>
      )}
      {openFiltersOptionsModal && (
        <FiltersOptionsModal
          filtersToApply="payments"
          showNoveltyTypeFilter={false}
          customersData={customersData}
          handleDialogOpenState={(openState: boolean) => {
            setOpenFiltersOptionsModal(openState)
          }}
          title={t('filtersOptionsModal.payments.title', { ns: 'common' })}
          actionButtonText={t('filtersOptionsModal.payments.actionButtonText', { ns: 'common' })}
        />
      )}
    </div>
  )
}

export default PaymentsCompletedManagement
